import {
  combineLatest as observableCombineLatest,
  forkJoin as observableForkJoin,
  of as observableOf,
  Observable,
  BehaviorSubject,
  of,
} from 'rxjs';

import {first, mergeMap, catchError, map} from 'rxjs/operators';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  Directive,
} from '@angular/core';
import {Resource, ResourceWithPermissionHints} from '../resource';
import {ViewConfig} from './viewconfig';
import {LanguageService} from '../../language/language.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ResourceDetailSummaryDialogComponent} from './detail-summary-dialog.component';
import {CacheService} from '../../cache/cache.service';
import {DisplayAttribute} from '../displayattribute';
import {ResourceService, ResourceUpdateStatus} from '../resource.service';
import {DialogsService} from '../../shared/dialog/dialogs.service';
import {AddViewEvent} from '../events/addviewevent';
import {UpdateResourceEvent} from '../events/updateresourceevent';
import {UpdateLocalizedValueEvent} from '../events/updatelocalizedvalueevent';
import {UpdateLocaleEvent} from '../events/updatelocaleevent';
import {MultivalueDialogResult} from './controls/multivalue-dialog/multivalue-dialog-result';
import {SmdDataRowMark} from '../../shared/component/smd-datatable';
import {DetailTabconfig} from './detail-tabconfig';
import {UIError} from '../../error/uierror';
import {ErrorService} from '../../error/error.service';
import {getApiError} from '../../error/api-error';
import {EnvironmentConfigService} from '../../core/services/environment-config.service';
import {FilterResult} from '../filterresult';
import {getResourceLabel} from '../resource-label';

@Directive()
export abstract class ResourceDetailAbstractComponent implements OnDestroy, AfterViewChecked {
  @Output() onAddView: EventEmitter<AddViewEvent> = new EventEmitter<AddViewEvent>();
  @Output() onCloseView: EventEmitter<{RefreshActive: boolean}> = new EventEmitter<{
    RefreshActive: boolean;
  }>();
  @Output() onDataChanged: EventEmitter<string[]> = new EventEmitter<string[]>();

  public freshResource: boolean = true;
  public editEnabled: boolean = false;

  waitSpinner: boolean = false;

  viewConfig: Observable<ViewConfig> = null;

  resource: BehaviorSubject<ResourceWithPermissionHints> = null;
  resLocale: BehaviorSubject<string> = new BehaviorSubject('');
  protected resourceLocaleChange: any = {};
  protected saveCopy: Resource = null;
  protected isCreate: boolean = false; //required for create wizard with required attributes
  protected isBatch: boolean = false;
  protected batchIds: string[] = null;
  protected smallView: boolean = false;

  protected alive: boolean = true;

  private _htmlElement: HTMLElement;

  constructor(
    protected cacheService: CacheService,
    protected snackBar: MatSnackBar,
    protected changeDetector: ChangeDetectorRef,
    protected dialog: MatDialog,
    protected dialogsService: DialogsService,
    protected resourceService: ResourceService,
    elementRef: ElementRef,
    private errorService: ErrorService,
    protected envConfig: EnvironmentConfigService,
  ) {
    this._htmlElement = elementRef.nativeElement;
  }

  ngAfterViewInit(): void {
    this.onResize();
    // TODO: Remove timeout fix for detail-abstract onResize (Problem on other system)
    [50, 100, 500, 1000, 1500, 5000].forEach((timeout: number) => {
      setTimeout(() => {
        this.onResize();
      }, timeout);
    });
  }

  ngAfterViewChecked(): void {
    this.onResize();
  }

  @HostListener('window:resize', ['$event.target'])
  onResize(): void {
    let elem: Element = this._htmlElement.getElementsByClassName('my-container')[0];
    if (elem != null) {
      if (elem.clientWidth < 400) {
        elem.classList.add('smallView');
        this.smallView = true;
      } else {
        elem.classList.remove('smallView');
        this.smallView = false;
      }
    }
  }

  ngOnDestroy(): void {
    this.alive = false;
    this.resetComponent();
  }

  scrollTop(): void {
    //document.body.scrollTop = document.documentElement.scrollTop = 0;
    const scrollTop =
      window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    if (scrollTop !== 0) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  get scrollTopText(): Observable<string> {
    return this.cacheService.getLanguageResource().pipe(
      map(lr => {
        return LanguageService.translate('DetailScrollTop', 'scroll to top', lr);
      }),
    );
  }

  saveClick(): void {
    const obs = this.save();
    if (obs != null)
      obs.pipe(catchError((error, caught) => this.handleError(error, caught))).subscribe();
  }

  openTimeline(event: MouseEvent): void {
    const resource: ResourceWithPermissionHints = this.resource.getValue();
    if (resource[this.envConfig.getAttributeName('ObjectID')] == null) return null;
    if (event.ctrlKey || event.button === 1) {
      window.open('#/timeline/' + resource[this.envConfig.getAttributeName('ObjectID')].Values);
    } else {
      this.onAddView.emit(
        new AddViewEvent('timeline', resource[this.envConfig.getAttributeName('ObjectID')].Values),
      );
    }
  }

  save(): Observable<boolean[]> {
    this.changeDetector.markForCheck();
    this.changeDetector.detectChanges();
    this.saveAllTabs();

    if (this.resource == null) return null;
    const resource: ResourceWithPermissionHints = this.resource.getValue();
    if (resource[this.envConfig.getAttributeName('ObjectID')] == null) return null;

    return this.getValidationErrorCount(true).pipe(
      mergeMap((errors: number) => {
        if (errors > 0) {
          this.cacheService
            .getLanguageResource()
            .pipe(
              catchError((error, caught) => this.handleError(error, caught)),
              first(),
            )
            .subscribe(strRes => {
              let msg: string;
              if (errors === 1) {
                msg = LanguageService.translate(
                  'DetailValidationErrorsSingle',
                  'You have an error. Please fix it.',
                  strRes,
                );
              } else {
                msg = LanguageService.translate(
                  'DetailValidationErrors',
                  'You have {0} errors. Please fix them.',
                  strRes,
                  ['' + errors],
                );
              }
              this.snackBar.open(msg, undefined, {duration: 5000});
            });
          return observableOf(null);
        } else {
          let changes: any = {};
          let changesSummary: ResourceValueChange[] = [];
          let changesSummaryMultiValue: ResourceValueChange[] = [];

          Object.keys(resource).forEach((key: string) => {
            let oldVal: any = null;
            if (this.saveCopy != null) {
              oldVal = this.saveCopy[key];
            } else if (
              [
                this.envConfig.getAttributeName('ObjectID'),
                this.envConfig.getAttributeName('ObjectType'),
                this.envConfig.getAttributeName('Creator'),
                this.envConfig.getAttributeName('CreatedTime'),
              ].indexOf(key) !== -1
            ) {
              return;
            }

            let newVal: any = resource[key].Values;
            let newSo: any = resource[key].SpecialObject;
            if (newSo instanceof MultivalueDialogResult) {
              if (newSo.Changes != null && newSo.Changes.length !== 0) {
                changes[key] = newVal;
                const daObs: Observable<DisplayAttribute> = this.cacheService.getDisplayAttribute(
                  key,
                  resource[this.envConfig.getAttributeName('ObjectType')].Values,
                );
                const attributeObs: Observable<string> = daObs.pipe(
                  map((da: DisplayAttribute) => (da == null ? null : da.Caption)),
                );
                const removed: any[] = newSo.Changes.filter(
                  rm => rm.mark === SmdDataRowMark.Remove,
                ).map(rm => rm.model);
                const added: any[] = newSo.Changes.filter(rm => rm.mark === SmdDataRowMark.Add).map(
                  rm => rm.model,
                );
                changesSummaryMultiValue.push(
                  new ResourceValueChange(attributeObs, daObs, removed, added),
                );
              }
            } else if (Array.isArray(newVal) || Array.isArray(oldVal)) {
              const daObs: Observable<DisplayAttribute> = this.cacheService.getDisplayAttribute(
                key,
                resource[this.envConfig.getAttributeName('ObjectType')].Values,
              );
              const attributeObs: Observable<string> = daObs.pipe(
                map((da: DisplayAttribute) => (da == null ? null : da.Caption)),
              );
              if (newVal === null || newVal === undefined) {
                changes[key] = null;
                changesSummaryMultiValue.push(
                  new ResourceValueChange(attributeObs, daObs, oldVal, []),
                );
              } else if (oldVal === null || oldVal === undefined) {
                changes[key] = newVal;
                changesSummaryMultiValue.push(
                  new ResourceValueChange(attributeObs, daObs, [], newVal),
                );
              } else if (
                JSON.stringify(oldVal.slice().sort()) !== JSON.stringify(newVal.slice().sort())
              ) {
                changes[key] = newVal;
                const removed: any[] = oldVal.filter(x => newVal.indexOf(x) === -1);
                const added: any[] = newVal.filter(x => oldVal.indexOf(x) === -1);
                changesSummaryMultiValue.push(
                  new ResourceValueChange(attributeObs, daObs, removed, added),
                );
              }
            } else if (
              ((oldVal === null || oldVal === undefined) &&
                !(newVal === null || newVal === undefined)) ||
              (!(oldVal === null || oldVal === undefined) &&
                JSON.stringify(newVal) !== JSON.stringify(oldVal))
            ) {
              changes[key] = newVal;
              const daObs: Observable<DisplayAttribute> = this.cacheService.getDisplayAttribute(
                key,
                resource[this.envConfig.getAttributeName('ObjectType')].Values,
              );
              const attributeObs: Observable<string> = daObs.pipe(
                map((da: DisplayAttribute) => (da == null ? null : da.Caption)),
              );
              changesSummary.push(
                new ResourceValueChange(attributeObs, daObs, oldVal, changes[key]),
              );
            }
          });

          Object.keys(this.resourceLocaleChange).forEach((locale: string) => {
            Object.keys(this.resourceLocaleChange[locale]).forEach((attr: string) => {
              const daObs: Observable<DisplayAttribute> = this.cacheService.getDisplayAttribute(
                attr,
                resource[this.envConfig.getAttributeName('ObjectType')].Values,
              );
              if (locale === '*' || locale === 'NeutralLocale') {
                changesSummary.push(
                  new ResourceValueChange(
                    this.cacheService.getLanguageResource().pipe(
                      map(lr => {
                        return (
                          attr +
                          ' (' +
                          LanguageService.translate('DetailSummaryNeutralLocale', 'Neutral', lr) +
                          ')'
                        );
                      }),
                    ),
                    daObs,
                    '⟨ Unknown ⟩',
                    this.resourceLocaleChange[locale][attr],
                  ),
                );
              } else {
                changesSummary.push(
                  new ResourceValueChange(
                    observableOf(attr + ' (' + locale + ')'),
                    daObs,
                    '⟨ Unknown ⟩',
                    this.resourceLocaleChange[locale][attr],
                  ),
                );
              }
            });
          });

          if (changesSummary.length === 0 && changesSummaryMultiValue.length === 0) {
            this.cacheService
              .getLanguageResource()
              .pipe(
                catchError((error, caught) => this.handleError(error, caught)),
                first(),
              )
              .subscribe(strRes => {
                let msg: string = LanguageService.translate(
                  'DetailSaveNothingChanged',
                  'You didn’t change anything. Nothing saved.',
                  strRes,
                );
                this.snackBar.open(msg, undefined, {duration: 5000});
              });
            this.editEnabled = false;
            return observableOf([true]);
          }

          return this.viewConfig.pipe(
            first(),
            mergeMap((btc: ViewConfig) => {
              if (btc.HasSummary) {
                return this.cacheService.getLanguageResource().pipe(
                  mergeMap(strRes => {
                    let dialogRef: MatDialogRef<ResourceDetailSummaryDialogComponent> =
                      this.dialog.open(ResourceDetailSummaryDialogComponent);
                    if (this.isBatch) {
                      let count: string = '' + this.batchIds.length;
                      let title: string = LanguageService.translate(
                        'BatchSummaryTitle',
                        '{0} resources',
                        strRes,
                        [count],
                      );
                      dialogRef.componentInstance.displayName = title;
                    } else {
                      if (
                        resource[this.envConfig.getAttributeName('DisplayName')] == null ||
                        resource[this.envConfig.getAttributeName('DisplayName')].Values == null
                      ) {
                        dialogRef.componentInstance.displayName = null;
                      } else {
                        dialogRef.componentInstance.displayName =
                          resource[this.envConfig.getAttributeName('DisplayName')].Values;
                      }
                    }
                    dialogRef.componentInstance.showOldValues = !this.isCreate;
                    dialogRef.componentInstance.changes = changesSummary;
                    dialogRef.componentInstance.changesMultiValue = changesSummaryMultiValue;
                    return dialogRef.afterClosed().pipe(
                      first(),
                      mergeMap((choice: any) => {
                        let obs: Observable<boolean[]>;
                        if (choice === true) {
                          this.editEnabled = false;
                          this.waitSpinner = true;
                          obs = this.saveResourceChanges(changes).pipe(
                            map(x => {
                              this.waitSpinner = false;
                              this.changeDetector.markForCheck();
                              return x;
                            }),
                          );
                        } else {
                          this.editEnabled = true;
                          obs = observableOf(null);
                        }
                        this.changeDetector.markForCheck();
                        return obs;
                      }),
                    );
                  }),
                );
              } else {
                this.editEnabled = false;
                this.waitSpinner = true;
                this.changeDetector.markForCheck();
                return this.saveResourceChanges(changes).pipe(
                  map(x => {
                    this.waitSpinner = false;
                    this.changeDetector.markForCheck();
                    return x;
                  }),
                );
              }
            }),
          );
        }
      }),
    );
  }

  protected saveResourceChanges(changes: any): Observable<boolean[]> {
    const resource: ResourceWithPermissionHints = this.resource.getValue();
    if (this.isCreate) {
      changes[this.envConfig.getAttributeName('ObjectType')] =
        resource[this.envConfig.getAttributeName('ObjectType')].Values;
    }
    if (this.isBatch) {
      const updObs: Observable<boolean[]>[] = [];
      this.batchIds.forEach((id: string) => {
        updObs.push(
          this.resourceService.getBasicResource(id).pipe(
            map((res: Resource) => res[this.envConfig.getAttributeName('DisplayName')]),
            mergeMap((batchTargetDisplayName: string) => {
              return this.resourceService.updateOrCreateResource(id, changes, this.isCreate).pipe(
                first(),
                mergeMap((status: ResourceUpdateStatus) => {
                  if (status === ResourceUpdateStatus.ApprovalPending) {
                    this.cacheService
                      .getLanguageResource()
                      .pipe(
                        catchError((error, caught) => this.handleError(error, caught)),
                        first(),
                      )
                      .subscribe(lr => {
                        if (
                          batchTargetDisplayName === null ||
                          batchTargetDisplayName === undefined
                        ) {
                          batchTargetDisplayName = LanguageService.translate(
                            'TimelineUnknownType',
                            '⟨  Unknown ⟩',
                            lr,
                          );
                        }
                        let message: string;

                        if (
                          resource[this.envConfig.getAttributeName('DisplayName')] == null ||
                          resource[this.envConfig.getAttributeName('DisplayName')].Values == null ||
                          resource[this.envConfig.getAttributeName('DisplayName')].Values.length ===
                            0
                        ) {
                          if (
                            resource[this.envConfig.getAttributeName('ObjectID')] == null ||
                            resource[this.envConfig.getAttributeName('ObjectID')].Values == null
                          ) {
                            message = LanguageService.translate(
                              'ResourceCreatePending',
                              'Resource creation: Approval pending',
                              lr,
                            );
                          } else {
                            message = LanguageService.translate(
                              'ResourceUpdatePendingID',
                              'Resource with ID {0}: Approval of update pending.',
                              lr,
                              [batchTargetDisplayName],
                            );
                          }
                        } else {
                          message = LanguageService.translate(
                            'ResourceUpdatePending',
                            'Update of Resource {0}: Approval pending',
                            lr,
                            [batchTargetDisplayName],
                          );
                        }
                        this.snackBar.open(message, undefined, {duration: 2000});
                      });

                    return observableOf([]);
                  } else if (status === ResourceUpdateStatus.Success) {
                    const updateObs: Observable<boolean>[] = [];
                    Object.keys(this.resourceLocaleChange).forEach((locale: string) => {
                      if (this.resourceLocaleChange[locale] == null) return;
                      let curObs = this.resourceService
                        .updateResource(
                          resource[this.envConfig.getAttributeName('ObjectID')].Values,
                          this.resourceLocaleChange[locale],
                          locale,
                        )
                        .pipe(
                          first(),
                          map((status: ResourceUpdateStatus) => {
                            // success or pending
                            this.resourceLocaleChange[locale] = null;
                            return true;
                          }),
                          catchError((error: any) => {
                            this.cacheService
                              .getLanguageResource()
                              .pipe(
                                catchError((error, caught) => this.handleError(error, caught)),
                                first(),
                              )
                              .subscribe(lr => {
                                if (
                                  batchTargetDisplayName === null ||
                                  batchTargetDisplayName === undefined
                                ) {
                                  batchTargetDisplayName = LanguageService.translate(
                                    'TimelineUnknownType',
                                    '⟨  Unknown ⟩',
                                    lr,
                                  );
                                }
                                let arg: string = '';
                                if (
                                  resource != null &&
                                  resource[this.envConfig.getAttributeName('DisplayName')] !=
                                    null &&
                                  resource[this.envConfig.getAttributeName('DisplayName')].Values !=
                                    null &&
                                  resource[this.envConfig.getAttributeName('DisplayName')].Values
                                    .length > 0
                                ) {
                                  arg =
                                    resource[this.envConfig.getAttributeName('DisplayName')].Values;
                                } else if (
                                  resource != null &&
                                  resource[this.envConfig.getAttributeName('ObjectID')] != null &&
                                  resource[this.envConfig.getAttributeName('ObjectID')].Values !=
                                    null &&
                                  resource[this.envConfig.getAttributeName('ObjectID')].Values
                                    .length > 0
                                ) {
                                  arg =
                                    resource[this.envConfig.getAttributeName('ObjectID')].Values;
                                }
                                let message: string = LanguageService.translate(
                                  'ResourceUpdateLocaleFailure',
                                  'Failed to update the resource {0} for locale {1}.',
                                  lr,
                                  [batchTargetDisplayName, locale],
                                );
                                this.snackBar.open(message, undefined, {duration: 5000});
                              });
                            return observableOf(false);
                          }),
                        );
                      updateObs.push(curObs);
                    });

                    this.cacheService
                      .getLanguageResource()
                      .pipe(
                        catchError((error, caught) => this.handleError(error, caught)),
                        first(),
                      )
                      .subscribe(lr => {
                        if (
                          batchTargetDisplayName === null ||
                          batchTargetDisplayName === undefined
                        ) {
                          batchTargetDisplayName = LanguageService.translate(
                            'TimelineUnknownType',
                            '⟨  Unknown ⟩',
                            lr,
                          );
                        }
                        let message: string;

                        if (
                          resource[this.envConfig.getAttributeName('DisplayName')] == null ||
                          resource[this.envConfig.getAttributeName('DisplayName')].Values == null ||
                          resource[this.envConfig.getAttributeName('DisplayName')].Values.length ===
                            0
                        ) {
                          if (
                            resource[this.envConfig.getAttributeName('ObjectID')] == null ||
                            resource[this.envConfig.getAttributeName('ObjectID')].Values == null
                          ) {
                            message = LanguageService.translate(
                              'ResourceCreateSuccess',
                              'Resource created successfully',
                              lr,
                            );
                          } else {
                            message = LanguageService.translate(
                              'ResourceUpdateSuccessID',
                              'Resource with ID {0} updated successfully.',
                              lr,
                              [batchTargetDisplayName],
                            );
                          }
                        } else {
                          message = LanguageService.translate(
                            'ResourceUpdateSuccess',
                            'Resource {0} updated successfully.',
                            lr,
                            [batchTargetDisplayName],
                          );
                        }
                        this.snackBar.open(message, undefined, {duration: 2000});
                      });
                    if (this.saveCopy != null) {
                      Object.keys(changes).forEach((key: string) => {
                        if (Array.isArray(changes[key])) {
                          this.saveCopy[key] = changes[key].slice();
                        } else {
                          this.saveCopy[key] = changes[key];
                        }
                      });
                    }
                    let res: ResourceWithPermissionHints = this.resource.getValue();
                    Object.keys(res).forEach((key: string) => {
                      res[key].SpecialObject = null;
                    });
                    this.resource.next(res);
                    this.freshResource = true;
                    if (updateObs.length > 0) {
                      return observableForkJoin(updateObs).pipe(
                        map(x => {
                          this.onDataChanged.emit(changes);
                          return x;
                        }),
                      );
                    } else {
                      this.onDataChanged.emit(changes);
                      return observableOf([]);
                    }
                  } else {
                    return observableOf(null);
                  }
                }),
              );
            }),
            catchError((error: any) => {
              console.error(error);
              this.editEnabled = true;
              return this.resourceService.getBasicResource(id).pipe(
                map((res: Resource) => {
                  let arg: string = '';
                  if (res != null && res[this.envConfig.getAttributeName('DisplayName')]) {
                    arg = res[this.envConfig.getAttributeName('DisplayName')];
                  } else if (
                    res != null &&
                    res[this.envConfig.getAttributeName('ObjectID')] != null
                  ) {
                    arg = res[this.envConfig.getAttributeName('ObjectID')];
                  }
                  return arg;
                }),
                mergeMap((batchTargetDisplayName: string) => {
                  return this.cacheService.getLanguageResource().pipe(
                    first(),
                    mergeMap(lr => {
                      const title: string = LanguageService.translate(
                        'ResourceUpdateFailureTitle',
                        'Saving changes failed.',
                        lr,
                      );
                      let message: string = LanguageService.translate(
                        'ResourceUpdateFailureMessage',
                        'Failed to update the resource {0}.',
                        lr,
                        [batchTargetDisplayName],
                      );
                      const apiError = getApiError(error);
                      if (apiError.code != null) {
                        message = LanguageService.getErrorMessage(apiError.code, lr);
                      }
                      return this.dialogsService
                        .webserviceError(apiError.message ?? error, title, message, lr)
                        .pipe(
                          map(() => {
                            this.editEnabled = true;
                            this.onResize();
                            this.changeDetector.markForCheck();
                            return null;
                          }),
                        );
                    }),
                  );
                }),
              );
            }),
          ),
        );
      });
      return observableForkJoin(updObs).pipe(
        map((bools: boolean[][]) => {
          let finalBools: boolean[] = [];
          if (bools) {
            bools.forEach((boolArr: boolean[]) => {
              if (boolArr) {
                boolArr.forEach((bool: boolean) => {
                  finalBools.push(bool);
                });
              }
            });
          }
          return finalBools;
        }),
      );
    }
    return this.resourceService
      .updateOrCreateResourceAndGetId(
        resource[this.envConfig.getAttributeName('ObjectID')].Values,
        changes,
        this.isCreate,
      )
      .pipe(
        first(),
        mergeMap((status: ResourceUpdateStatus) => {
          if (status === ResourceUpdateStatus.ApprovalPending) {
            this.cacheService
              .getLanguageResource()
              .pipe(
                catchError((error, caught) => this.handleError(error, caught)),
                first(),
              )
              .subscribe(lr => {
                let message: string;

                if (
                  resource[this.envConfig.getAttributeName('DisplayName')] == null ||
                  resource[this.envConfig.getAttributeName('DisplayName')].Values == null ||
                  resource[this.envConfig.getAttributeName('DisplayName')].Values.length === 0
                ) {
                  if (
                    resource[this.envConfig.getAttributeName('ObjectID')] == null ||
                    resource[this.envConfig.getAttributeName('ObjectID')].Values == null
                  ) {
                    message = LanguageService.translate(
                      'ResourceCreatePending',
                      'Resource creation: Approval pending',
                      lr,
                    );
                  } else {
                    message = LanguageService.translate(
                      'ResourceUpdatePendingID',
                      'Resource with ID {0}: Approval of update pending.',
                      lr,
                      [resource[this.envConfig.getAttributeName('ObjectID')].Values],
                    );
                  }
                } else {
                  message = LanguageService.translate(
                    'ResourceUpdatePending',
                    'Update of Resource {0}: Approval pending',
                    lr,
                    [resource[this.envConfig.getAttributeName('DisplayName')].Values],
                  );
                }
                this.snackBar.open(message, undefined, {duration: 2000});
                this.freshResource = true;
                this.onApprovalPendingSaved();
              });

            return observableOf(true);
          } else if (
            status === ResourceUpdateStatus.Success ||
            (typeof status === 'string' && status != null)
          ) {
            const updateObs: Observable<boolean>[] = [];
            Object.keys(this.resourceLocaleChange).forEach((locale: string) => {
              if (this.resourceLocaleChange[locale] == null) return;
              let id = resource[this.envConfig.getAttributeName('ObjectID')].Values;
              if ((this.isCreate || id == null) && typeof status === 'string') {
                id = status;
              }
              let curObs = this.resourceService
                .updateResource(id, this.resourceLocaleChange[locale], locale)
                .pipe(
                  first(),
                  map((status: ResourceUpdateStatus) => {
                    // success or pending
                    this.resourceLocaleChange[locale] = null;
                    return true;
                  }),
                  catchError((error: any) => {
                    this.cacheService
                      .getLanguageResource()
                      .pipe(
                        catchError((error, caught) => this.handleError(error, caught)),
                        first(),
                      )
                      .subscribe(lr => {
                        let arg: string = '';
                        if (
                          resource != null &&
                          resource[this.envConfig.getAttributeName('DisplayName')] != null &&
                          resource[this.envConfig.getAttributeName('DisplayName')].Values != null &&
                          resource[this.envConfig.getAttributeName('DisplayName')].Values.length > 0
                        ) {
                          arg = resource[this.envConfig.getAttributeName('DisplayName')].Values;
                        } else if (
                          resource != null &&
                          resource[this.envConfig.getAttributeName('ObjectID')] != null &&
                          resource[this.envConfig.getAttributeName('ObjectID')].Values != null &&
                          resource[this.envConfig.getAttributeName('ObjectID')].Values.length > 0
                        ) {
                          arg = resource[this.envConfig.getAttributeName('ObjectID')].Values;
                        }
                        let message: string = LanguageService.translate(
                          'ResourceUpdateLocaleFailure',
                          'Failed to update the resource {0} for locale {1}.',
                          lr,
                          [arg, locale],
                        );
                        this.snackBar.open(message, undefined, {duration: 5000});
                      });
                    return observableOf(false);
                  }),
                );
              updateObs.push(curObs);
            });

            this.cacheService
              .getLanguageResource()
              .pipe(
                catchError((error, caught) => this.handleError(error, caught)),
                first(),
              )
              .subscribe(lr => {
                let message: string;

                if (
                  resource[this.envConfig.getAttributeName('DisplayName')] == null ||
                  resource[this.envConfig.getAttributeName('DisplayName')].Values == null ||
                  resource[this.envConfig.getAttributeName('DisplayName')].Values.length === 0
                ) {
                  if (
                    this.isCreate ||
                    resource[this.envConfig.getAttributeName('ObjectID')] == null ||
                    resource[this.envConfig.getAttributeName('ObjectID')].Values == null
                  ) {
                    message = LanguageService.translate(
                      'ResourceCreateSuccess',
                      'Resource created successfully',
                      lr,
                    );
                  } else {
                    message = LanguageService.translate(
                      'ResourceUpdateSuccessID',
                      'Resource with ID {0} updated successfully.',
                      lr,
                      [resource[this.envConfig.getAttributeName('ObjectID')].Values],
                    );
                  }
                } else if (this.isCreate) {
                  message = LanguageService.translate(
                    'ResourceCreateSuccessDN',
                    'Resource {0} created successfully.',
                    lr,
                    [resource[this.envConfig.getAttributeName('DisplayName')].Values],
                  );
                } else {
                  message = LanguageService.translate(
                    'ResourceUpdateSuccess',
                    'Resource {0} updated successfully.',
                    lr,
                    [resource[this.envConfig.getAttributeName('DisplayName')].Values],
                  );
                }
                this.snackBar.open(message, undefined, {duration: 2000});
              });
            if (this.saveCopy != null) {
              Object.keys(changes).forEach((key: string) => {
                if (Array.isArray(changes[key])) {
                  this.saveCopy[key] = changes[key].slice();
                } else {
                  this.saveCopy[key] = changes[key];
                }
              });
            }
            let res: ResourceWithPermissionHints = this.resource.getValue();
            Object.keys(res).forEach((key: string) => {
              res[key].SpecialObject = null;
            });
            this.resource.next(res);
            this.freshResource = true;

            if (updateObs.length > 0) {
              return observableForkJoin(updateObs).pipe(
                map(x => {
                  this.onDataChanged.emit(changes);
                  return x;
                }),
              );
            } else {
              this.onDataChanged.emit(changes);
              return observableOf([]);
            }
          } else {
            return observableOf(null);
          }
        }),
        catchError((error: any) => {
          console.error(error);
          this.editEnabled = true;
          this.waitSpinner = false;
          return this.cacheService.getLanguageResource().pipe(
            first(),
            mergeMap(lr => {
              let arg: string = '';
              if (
                resource != null &&
                resource[this.envConfig.getAttributeName('DisplayName')] != null &&
                resource[this.envConfig.getAttributeName('DisplayName')].Values != null &&
                resource[this.envConfig.getAttributeName('DisplayName')].Values.length > 0
              ) {
                arg = resource[this.envConfig.getAttributeName('DisplayName')].Values;
              } else if (
                resource != null &&
                resource[this.envConfig.getAttributeName('ObjectID')] != null &&
                resource[this.envConfig.getAttributeName('ObjectID')].Values != null &&
                resource[this.envConfig.getAttributeName('ObjectID')].Values.length > 0
              ) {
                arg = resource[this.envConfig.getAttributeName('ObjectID')].Values;
              }

              const title: string = LanguageService.translate(
                'ResourceUpdateFailureTitle',
                'Saving changes failed.',
                lr,
              );
              let message: string = LanguageService.translate(
                'ResourceUpdateFailureMessage',
                'Failed to update the resource {0}.',
                lr,
                [arg],
              );
              const apiError = getApiError(error);
              if (apiError.code != null) {
                message = LanguageService.getErrorMessage(apiError.code, lr);
              }

              return this.dialogsService
                .webserviceError(apiError.message ?? error, title, message, lr)
                .pipe(
                  map(() => {
                    this.editEnabled = true;
                    this.onResize();
                    this.changeDetector.markForCheck();
                    return null;
                  }),
                );
            }),
          );
        }),
      );
  }

  /**
   * Called after a single-object save was accepted with "approval pending" (202). By default the
   * view closes without refreshing the list; a subclass can override this to stay open.
   */
  protected onApprovalPendingSaved(): void {
    this.onCloseView.emit({RefreshActive: false});
  }

  approve(type: any): Observable<boolean[]> {
    if (this.resource == null) return null;
    const resource: ResourceWithPermissionHints = this.resource.getValue();
    if (resource[this.envConfig.getAttributeName('ObjectID')] == null) return null;

    return this.getValidationErrorCount(true).pipe(
      mergeMap((errors: number) => {
        if (errors > 0) {
          this.cacheService
            .getLanguageResource()
            .pipe(
              catchError((error, caught) => this.handleError(error, caught)),
              first(),
            )
            .subscribe(strRes => {
              let msg: string;
              if (errors === 1) {
                msg = LanguageService.translate(
                  'DetailValidationErrorsSingle',
                  'You have an error. Please fix it.',
                  strRes,
                );
              } else {
                msg = LanguageService.translate(
                  'DetailValidationErrors',
                  'You have {0} errors. Please fix them.',
                  strRes,
                  ['' + errors],
                );
              }
              this.snackBar.open(msg, undefined, {duration: 5000});
            });
          return observableOf(null);
        } else {
          this.editEnabled = false;
          this.waitSpinner = true;
          this.changeDetector.markForCheck();

          return this.approveResourceChanges(type).pipe(
            map(x => {
              this.waitSpinner = false;
              this.changeDetector.markForCheck();
              return x;
            }),
          );
        }
      }),
    );
  }

  protected approveResourceChanges(type: any): Observable<boolean[]> {
    const resource: ResourceWithPermissionHints = this.resource.getValue();
    const updObs: Observable<boolean>[] = [];
    this.batchIds.forEach((guid: string) => {
      if (ResourceService.guidRegex.test(guid)) {
        updObs.push(
          this.resourceService
            .searchResources(
              '/' +
                this.envConfig.getObjectTypeName('Approval') +
                '[' +
                this.envConfig.getAttributeName('ObjectID') +
                "='" +
                guid +
                "']",
              this.envConfig.getAttributeName('Request') + ',ApprovalStatus',
              1,
            )
            .pipe(
              catchError((error, caught) => {
                return observableOf(null);
              }),
              mergeMap((approval: FilterResult) => {
                if (
                  approval != null &&
                  approval.TotalCount === 1 &&
                  approval.Results.length === 1
                ) {
                  const app = approval.Results[0];
                  if (app[this.envConfig.getAttributeName('ApprovalStatus')] === 'Pending') {
                    return this.resourceService
                      .requestApproval(
                        guid,
                        type,
                        resource[this.envConfig.getAttributeName('Reason')].Values,
                      )
                      .pipe(
                        catchError((error, caught) => {
                          return [false];
                        }),
                      );
                  } else {
                    this.cacheService
                      .getLanguageResource()
                      .pipe(first())
                      .subscribe(lr => {
                        const title = LanguageService.translate(
                          'RequestSubmitAlreadyProcessedTitle',
                          'Approval already completed',
                          lr,
                        );
                        let message: string = LanguageService.translate(
                          'RequestSubmitAlreadyProcessed',
                          'Approval is already approved or rejected.',
                          lr,
                        );
                        this.dialogsService.error(title, message, null);
                      });
                    return observableOf(true);
                  }
                } else {
                  return observableOf(true);
                }
              }),
            ),
        );
      }
    });
    return observableForkJoin(updObs).pipe(
      map((results: boolean[]) => {
        let errorCount: number = 0;
        if (results == null) {
          errorCount = -1;
        } else {
          results.forEach((ok: boolean) => {
            if (ok === false) errorCount++;
          });
        }
        this.cacheService
          .getLanguageResource()
          .pipe(first())
          .subscribe(lr => {
            if (errorCount === 0) {
              const message = LanguageService.translate(
                'RequestSubmitSuccessMessage',
                'Your request decision has been submitted successfully.',
                lr,
              );
              this.snackBar.open(message, undefined, {duration: 5000});
            } else {
              const title = LanguageService.translate(
                'RequestSubmitErrorTitle',
                'Error submitting request decision',
                lr,
              );
              let message: string;
              if (errorCount === -1) {
                message = LanguageService.translate(
                  'RequestSubmitErrorMessageFatal',
                  'A fatal error occurred during submitting your request decision.',
                  lr,
                );
              } else if (errorCount === 1) {
                message = LanguageService.translate(
                  'RequestSubmitErrorMessageSingle',
                  'Request decision could not be submitted.',
                  lr,
                );
              } else {
                message = LanguageService.translate(
                  'RequestSubmitErrorMessageFatal',
                  '{0} of your request decisions could not be submitted.',
                  lr,
                  ['' + errorCount],
                );
              }
              this.dialogsService.error(title, message, null);
            }
          });
        return results;
      }),
    );
  }

  public abstract getValidationErrorCount(includeNonTouched: boolean): Observable<number>;

  public updateValidity(): void {
    this.getValidationErrorCount(false).pipe(first()).subscribe();
  }

  protected abstract saveAllTabs(): void;

  protected abstract resetComponent(): void;

  updateResource(updateResourceEvent: UpdateResourceEvent) {
    if (this.freshResource === true) {
      this.freshResource = false;
      // fire all controls to get generated values (source != target)
      this.saveAllTabs();
    }

    const resource: ResourceWithPermissionHints = this.resource.getValue();
    let newVal: any = updateResourceEvent.value;
    if (newVal instanceof MultivalueDialogResult) {
      if (resource[updateResourceEvent.path].Values !== newVal.CurrentlySelected) {
        resource[updateResourceEvent.path].Values = newVal.CurrentlySelected;
        resource[updateResourceEvent.path].SpecialObject = newVal;
        this.resource.next(resource);
      }
    } else if (resource[updateResourceEvent.path].Values !== updateResourceEvent.value) {
      if (
        Array.isArray(updateResourceEvent.value) &&
        Array.isArray(resource[updateResourceEvent.path].Values) &&
        JSON.stringify(updateResourceEvent.value.slice().sort()) ===
          JSON.stringify(resource[updateResourceEvent.path].Values.slice().sort())
      ) {
        // skip array update if content didn't change, references change always
        return;
      }

      this.cacheService
        .getDisplayAttribute(
          updateResourceEvent.path,
          this.resource.getValue()[this.envConfig.getAttributeName('ObjectType')].Values,
        )
        .pipe(
          catchError((err, caught) => this.handleError(err, caught)),
          first(),
        )
        .subscribe(da => {
          if (da.Multivalued === true && !Array.isArray(updateResourceEvent.value)) {
            if (updateResourceEvent.value == null) {
              resource[updateResourceEvent.path].Values = [];
            } else {
              resource[updateResourceEvent.path].Values = [updateResourceEvent.value];
            }
          } else if (da.Multivalued === false && Array.isArray(updateResourceEvent.value)) {
            if (updateResourceEvent.value.length === 0) {
              resource[updateResourceEvent.path].Values = null;
            } else {
              resource[updateResourceEvent.path].Values = updateResourceEvent.value[0];
            }
          } else {
            resource[updateResourceEvent.path].Values = updateResourceEvent.value;
          }
          this.resource.next(resource);
        });
    }
  }

  updateLocalizedValue(updateLocalizedValueEvent: UpdateLocalizedValueEvent) {
    const locale: string = this.resLocale.getValue();
    if (locale == null || locale === '') {
      // ignore locale null - no locale selected
      // important because saveAllTabs() triggers updates on all controls, including locales
      return;
    }

    if (this.resourceLocaleChange[locale] == null) {
      this.resourceLocaleChange[locale] = {};
    }

    this.cacheService
      .getDisplayAttribute(
        updateLocalizedValueEvent.path,
        this.resource.getValue()[this.envConfig.getAttributeName('ObjectType')].Values,
      )
      .pipe(
        catchError((err, caught) => this.handleError(err, caught)),
        first(),
      )
      .subscribe(da => {
        if (da.Multivalued === true && !Array.isArray(updateLocalizedValueEvent.value)) {
          if (updateLocalizedValueEvent.value == null) {
            this.resourceLocaleChange[locale][updateLocalizedValueEvent.path] = [];
          } else {
            this.resourceLocaleChange[locale][updateLocalizedValueEvent.path] = [
              updateLocalizedValueEvent.value,
            ];
          }
        } else if (da.Multivalued === false && Array.isArray(updateLocalizedValueEvent.value)) {
          if (updateLocalizedValueEvent.value.length === 0) {
            this.resourceLocaleChange[locale][updateLocalizedValueEvent.path] = null;
          } else {
            this.resourceLocaleChange[locale][updateLocalizedValueEvent.path] =
              updateLocalizedValueEvent.value[0];
          }
        } else {
          this.resourceLocaleChange[locale][updateLocalizedValueEvent.path] =
            updateLocalizedValueEvent.value;
        }
      });
  }

  updateLocale(updateLocale: UpdateLocaleEvent) {
    this.resLocale.next(updateLocale.locale);
  }

  noTabVisible(): Observable<boolean> {
    const retObs = this.viewConfig.pipe(
      mergeMap((vc: ViewConfig) => {
        if (vc == null || vc.Details == null) {
          //other error message gets displayed
          return observableOf(false);
        }

        return vc.Details.pipe(
          mergeMap((dtcs: DetailTabconfig[]) => {
            if (dtcs == null || dtcs.length === 0) return observableOf(true);
            const obsArr: Observable<boolean>[] = [];
            dtcs.forEach((dtc: DetailTabconfig) => {
              const obs = observableCombineLatest(
                dtc.Visible,
                dtc.HasChilds,
                (visible: boolean, hasChildren: boolean) => {
                  return visible === true && hasChildren === true;
                },
              );
              obsArr.push(obs);
            });
            return observableCombineLatest(obsArr).pipe(
              map((visibles: boolean[]) => {
                for (let i = 0; i < visibles.length; i++) {
                  if (visibles[i] === true) {
                    return false;
                  }
                }
                return true;
              }),
            );
          }),
        );
      }),
    );
    return this.getCaughtObservable(retObs, false);
  }

  noDialogConf(): Observable<boolean> {
    const retObs = this.viewConfig.pipe(
      mergeMap((vc: ViewConfig) => {
        if (vc == null || vc.Details == null) {
          return observableOf(true);
        }
        return observableOf(false);
      }),
    );
    return this.getCaughtObservable(retObs, false);
  }

  get allowAdvancedDetail(): Observable<boolean> {
    return this.cacheService.getCurrentUser().pipe(mergeMap(u => u.administrator));
  }

  get allowTimeline(): Observable<boolean> {
    if (this.resource == null) return observableOf(false);
    return this.resource.pipe(map(res => this.canReadTimeline(res)));
  }

  protected canReadTimeline(res: ResourceWithPermissionHints): boolean {
    if (res == null) return false;
    const attr = res[this.envConfig.getAttributeName('Timeline')];
    return (
      attr != null && attr.PermissionHint != null && attr.PermissionHint.indexOf('Read') !== -1
    );
  }

  advancedDetail: boolean = false;

  toggleAdvancedDetail(): void {
    this.advancedDetail = !this.advancedDetail;
  }

  getCaughtObservable(obs: Observable<any>, fatal: boolean): Observable<any> {
    if (obs == null) return null;
    return obs.pipe(
      catchError((error, caught) =>
        fatal ? this.handleFatalError(error, caught) : this.handleError(error, caught),
      ),
    );
  }

  handleError(error: any, caught?: Observable<any>) {
    console.error(error);
    try {
      let code = error.name;

      if (status in error) {
        code = error.status;
      }
      this.errorService.reportError(new UIError(error.name, code, error.message, 'View'));
    } catch (error) {
      console.warn('Could not report error to service.');
    }
    setTimeout(() => {
      if (this.dialogsService != null && this.cacheService != null) {
        this.cacheService
          .getLanguageResource()
          .pipe(first())
          .subscribe(lr => {
            const title = LanguageService.translate('ResourceDetailErrorTitle', 'Error', lr);
            let msg = LanguageService.translate(
              'ResourceDetailErrorMessage',
              'There occurred an error.',
              lr,
            );
            this.dialogsService.webserviceError(error, title, msg, lr);
          });
      }
    });
    return observableOf(null);
  }

  handleFatalError(error: any, caught?: Observable<any>) {
    //report error
    console.error(error);
    try {
      let code = error.name;

      if (status in error) {
        code = error.status;
      }
      this.errorService.reportError(new UIError(error.name, code, error.message, 'View'));
    } catch (error) {
      console.warn('Could not report error to service.');
    }
    //show error message
    setTimeout(() => {
      if (this.dialogsService != null && this.cacheService != null) {
        this.cacheService
          .getLanguageResource()
          .pipe(first())
          .subscribe(
            lr => {
              let title = LanguageService.translate('ResourceDetailErrorTitle', 'Error', lr);
              let msg = LanguageService.translate(
                'ResourceDetailFatalErrorMessage',
                'There occurred an error in displaying the specified resource. The view will be closed.',
                lr,
              );
              if (
                !(error.name === null || error.name === undefined) &&
                (error.name === 'View.DialogConfigurationNotFoundError' ||
                  error.name === 'View.WizardConfigurationNotFoundError')
              ) {
                title = error.name;
                msg = error.message;
              } else if (
                !(error.code === null || error.code === undefined) &&
                error.message.indexOf('RequiredValueIsMissing') != -1
              ) {
                title = LanguageService.translate(
                  'ResourceDetailErrorRequiredValueTitle',
                  'Required Value Is Missing',
                  lr,
                );
                msg = LanguageService.translate(
                  'ResourceDetailFatalErrorRequiredValueMessage',
                  'An attribute is required to complete the operation.',
                  lr,
                );
              } else if (
                !(error.code === null || error.code === undefined) &&
                error.code === 'permission-denied'
              ) {
                title = LanguageService.translate(
                  'ResourceDetailErrorPermissionDeniedTitle',
                  'Permission Denied',
                  lr,
                );
                msg = LanguageService.translate(
                  'ResourceDetailFatalErrorPermissionDeniedMessage',
                  "The operation couldn't be completed. Permission denied.",
                  lr,
                );
              }

              this.dialogsService
                .webserviceError(error, title, msg, lr)
                .subscribe(() => this.onCloseView.emit({RefreshActive: false}));
            },
            () => this.onCloseView.emit({RefreshActive: false}),
          );
      }
    });
    return observableOf(null);
  }

  getResourceLabel() {
    return this.resource.pipe(
      mergeMap(resource => getResourceLabel(resource, this.cacheService, this.envConfig)),
    );
  }
}

export class ValidationInfo {
  private _errors: number;
  private _tabIndex: number;

  constructor(tabIndex: number, errors: number) {
    this._errors = errors;
    this._tabIndex = tabIndex;
  }

  get Errors(): number {
    return this._errors;
  }

  get TabIndex(): number {
    return this._tabIndex;
  }
}

export class ResourceValueChange {
  constructor(
    public AttributeName: Observable<string>,
    public DisplayAttribute: Observable<DisplayAttribute>,
    public OldValue: any,
    public NewValue: any,
  ) {}
}
